import { createColumnHelper } from '@tanstack/react-table';
import { Pencil, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { UserDto } from '../types';
import { UserStatusBadge } from './UserStatusBadge';

const columnHelper = createColumnHelper<UserDto>();

// Separate component to use hooks (hooks can't be called in column definitions)
function ActionCell({ user }: { user: UserDto }) {
  const navigate = useNavigate();

  if (user.isDefaultAdmin) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/administration/users/${user.id}`)}
              title="System Administrator account (protected)"
            >
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>System Administrator account (protected)</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => navigate(`/administration/users/${user.id}`)}
      title="Edit user"
    >
      <Pencil className="h-4 w-4" />
    </Button>
  );
}

export const columns = [
  columnHelper.accessor('username', {
    header: 'Username',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('fullName', {
    header: 'Full Name',
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('mobileNumber', {
    header: 'Mobile',
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('employeeIdNumber', {
    header: 'Employee ID',
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('departmentName', {
    header: 'Department',
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('positionName', {
    header: 'Position',
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('isActive', {
    header: 'Status',
    cell: (info) => <UserStatusBadge isActive={info.getValue()} />,
  }),
  columnHelper.accessor('lastLoginAt', {
    header: 'Last Login',
    cell: (info) => {
      const value = info.getValue();
      return value
        ? new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : 'Never';
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: (info) =>
      new Date(info.getValue()).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
  }),
  columnHelper.accessor('roles', {
    header: 'Roles',
    cell: (info) => {
      const roles = info.getValue();
      return roles.length > 0 ? roles.join(', ') : '-';
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: '',
    cell: ({ row }) => <ActionCell user={row.original} />,
  }),
];
